import { Reveal } from "@/lib/motion";
import { BOOK_REVIEW_MAILTO, CONTACT_EMAIL, LOCATION } from "@/lib/brand";
import { Mail, MapPin, Calendar } from "lucide-react";

export default function Contact() {
  return (
    <main data-testid="contact-page" className="pt-32 md:pt-40">
      <section className="max-w-7xl mx-auto px-6 md:px-10">
        <Reveal>
          <div className="overline">Contact</div>
        </Reveal>
        <Reveal delay={1}>
          <h1 className="mt-6 font-serif-display text-5xl sm:text-6xl lg:text-8xl leading-[0.9] tracking-tightest max-w-5xl">
            Let's reimagine
            <em className="font-light text-ink-soft"> how your business works.</em>
          </h1>
        </Reveal>
        <Reveal delay={2}>
          <p className="mt-10 max-w-2xl text-lg text-ink/75 leading-relaxed">
            Send a short note about your business and what you'd like to change.
            We'll reply with a few honest thoughts and, if it's a fit, a time to
            run a Business Systems Review.
          </p>
        </Reveal>
      </section>

      <section className="mt-24 md:mt-32 max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7 space-y-6">
          <Reveal>
            <a
              href={`mailto:${CONTACT_EMAIL}?subject=Hello%20ETradingGroup`}
              data-testid="contact-email-card"
              className="group block bg-bg-muted hover:bg-ink hover:text-bg-base transition-colors p-8 md:p-10 border border-ink/10"
            >
              <div className="flex items-start justify-between gap-6">
                <div>
                  <div className="overline text-brand-red">Email</div>
                  <div className="mt-4 font-serif-display text-3xl md:text-4xl tracking-tight">
                    {CONTACT_EMAIL}
                  </div>
                  <p className="mt-4 text-[14.5px] opacity-75">
                    Usually a reply within one working day. Sydney hours.
                  </p>
                </div>
                <Mail size={28} strokeWidth={1.4} className="opacity-60 group-hover:opacity-100" />
              </div>
            </a>
          </Reveal>

          <Reveal delay={1}>
            <a
              href={BOOK_REVIEW_MAILTO}
              data-testid="contact-booking-card"
              className="group block bg-bg-ink text-bg-base hover:bg-brand-red transition-colors p-8 md:p-10"
            >
              <div className="flex items-start justify-between gap-6">
                <div>
                  <div className="overline text-bg-base/60">Book</div>
                  <div className="mt-4 font-serif-display text-3xl md:text-4xl tracking-tight">
                    Business Systems Review
                  </div>
                  <p className="mt-4 text-[14.5px] opacity-80 max-w-md">
                    A structured look at your business — offer, communication,
                    workflow, admin and AI opportunities — with a clear path
                    forward.
                  </p>
                </div>
                <Calendar size={28} strokeWidth={1.4} className="opacity-60 group-hover:opacity-100" />
              </div>
              <span className="mt-8 inline-flex items-center gap-2 text-[13px] tracking-[0.06em] font-medium">
                Send a booking enquiry <span aria-hidden>→</span>
              </span>
            </a>
          </Reveal>
        </div>

        <div className="lg:col-span-5 space-y-8">
          <Reveal>
            <div className="border-t border-ink pt-6">
              <div className="overline">Location</div>
              <div className="mt-4 flex items-start gap-3">
                <MapPin size={20} strokeWidth={1.4} className="text-brand-red mt-1" />
                <div>
                  <div className="font-serif-display text-2xl tracking-tight">
                    {LOCATION}
                  </div>
                  <div className="text-[14px] text-ink-soft mt-2">
                    Working with businesses across Australia, remotely and on-site.
                  </div>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={1}>
            <div className="border-t border-ink/10 pt-6">
              <div className="overline">What to include</div>
              <ul className="mt-5 space-y-3 text-[14.5px] text-ink/80">
                {[
                  "A sentence about your business",
                  "What's working — and what isn't",
                  "Where you'd like to be in 12 months",
                  "Tools and team size (if you know)",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 bg-brand-red rounded-full mt-2 shrink-0" />
                    {t}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>

          <Reveal delay={2}>
            <div className="border-t border-ink/10 pt-6">
              <div className="overline">Specialist enquiries</div>
              <ul className="mt-5 space-y-2 text-[14.5px]">
                <li>
                  <a
                    href="https://2tap.app/"
                    target="_blank"
                    rel="noreferrer noopener"
                    className="link-reveal text-ink hover:text-brand-red"
                  >
                    2Tap — visit 2tap.app
                  </a>
                </li>
                <li className="text-ink/70">Contracts Administration Services</li>
                <li className="text-ink/70">Sales &amp; Marketing Services</li>
                <li className="text-ink/70">AI Workflow Setup</li>
              </ul>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
